import { useCallback, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, Menu, MenuResponse, SoldOutProduct } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";

export interface MenuEditor {
  menu: Menu;
  busy: boolean;
  /**
   * Stores the next menu as a new version with the owner's code; true when it was saved. When the
   * menu changed meanwhile on another device, the menu is loaded again and nothing is overwritten.
   */
  commit: (next: Menu, change: string) => Promise<boolean>;
  /** Sets a product sold out or available again (the same as Beheer › Uitverkocht). */
  setSoldOut: (productId: string, soldOut: boolean) => Promise<void>;
  /** The products that are sold out now (ids). */
  soldOut: ReadonlySet<string>;
  /** Says what happened, where these screens stand: on the till, or in Tafo-beheer for a shop. */
  toast: (message: string, tone?: "info" | "bad") => void;
}

/**
 * Saving in Beheer › Menu & prijzen: every change is active at once on every device (prototype). The
 * screens of Menu & prijzen only know this editor, so Tafo-beheer shows the same screens for a shop with
 * an editor of its own (Furkan, 05/10/2026: "alles zoals in de kassa").
 */
export function useMenuEditor(authorization: Authorization): MenuEditor {
  const { api, menu: current, menuChanged, reloadMenu, soldOut, soldOutChanged, report, toast } = useTill();
  const [busy, setBusy] = useState(false);
  const menu = current.menu!;

  const commit = useCallback(
    async (next: Menu, change: string) => {
      setBusy(true);
      try {
        const saved = await api.request<MenuResponse>("PUT", "/device/menu", {
          staffToken: authorization.token,
          body: { expectedVersion: current.version, menu: next, change },
        });
        menuChanged(saved);
        return true;
      } catch (error) {
        if (error instanceof ApiError && error.code === "menu_changed") {
          reloadMenu();
          toast(t.menuAdmin.changed, "bad");
        } else report(error);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [api, authorization.token, current.version, menuChanged, reloadMenu, report, toast],
  );

  const setSoldOut = useCallback(
    async (productId: string, soldOut: boolean) => {
      const { products } = await api.request<{ products: SoldOutProduct[] }>(
        "PUT",
        `/device/sold-out/${encodeURIComponent(productId)}`,
        { staffToken: authorization.token, body: { soldOut } },
      );
      soldOutChanged(products);
    },
    [api, authorization.token, soldOutChanged],
  );

  return { menu, busy, commit, setSoldOut, soldOut, toast };
}
